import React, { useEffect, useMemo, useRef, useState } from 'react';

/* =============================================================
   Canvas Vulnerability Writeup
   CVE-2026-31431  —  Linux kernel algif_aead
   Audience: CS161 students
   ============================================================= */

const CanvasVulnerability: React.FC = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'CVE-2026-31431 — How ShinyHunters got root on Canvas';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-emerald-400/30">
      <PageNav />
      <main>
        <HeroSection />
        <TldrSection />
        <SpheresSection />
        <AeadSection />
        <AlgifSection />
        <InPlaceSection />
        <BugSection />
        <ExploitChainSection />
        <PatchSection />
        <LessonsSection />
      </main>
      <PageFooter />
    </div>
  );
};

export default CanvasVulnerability;

/* ============================== NAV ============================== */

const PageNav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-slate-950/85 backdrop-blur border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <a
          href="/"
          className="group flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
        >
          <span className="text-slate-500 group-hover:-translate-x-0.5 transition-transform">←</span>
          <span>Nazar Ospanov</span>
        </a>
        <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono">
          <span className="px-2 py-1 rounded-md bg-rose-500/10 text-rose-300 border border-rose-500/20">
            CVE-2026-31431
          </span>
          <span className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-200 border border-amber-500/20">
            CVSS 7.8 · HIGH
          </span>
        </div>
      </div>
    </nav>
  );
};

/* ============================== HERO ============================== */

const HeroSection: React.FC = () => {
  return (
    <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <BackgroundGrid />
      <div className="relative max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-6 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
          <span className="inline-block w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
          <span>Vulnerability writeup</span>
          <span className="text-slate-700">·</span>
          <span>CS 161 friendly</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-white">
          How ShinyHunters got <span className="italic font-serif text-emerald-300">root</span> on Canvas
        </h1>
        <p className="mt-6 max-w-3xl text-lg sm:text-xl text-slate-300 leading-relaxed">
          A walk through <span className="font-mono text-emerald-300">CVE-2026-31431</span> — a
          Linux kernel bug in the AEAD socket interface that turned a low-privileged shell into a
          full kernel takeover. We'll build it up from the CS 161 ideas you already know:
          authenticated encryption, trust boundaries, and memory safety.
        </p>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
          <StatBadge label="Component" value="algif_aead" mono />
          <StatBadge label="Class" value="CWE-669" sub="Resource sphere mix-up" mono />
          <StatBadge label="Vector" value="Local" sub="AV:L · PR:L · UI:N" mono />
          <StatBadge label="Impact" value="C/I/A : H" sub="Full kernel takeover" mono />
        </div>

        <div className="mt-10 text-sm text-slate-400 max-w-3xl">
          <span className="text-slate-300 font-medium">Why read this?</span> Canvas was breached
          through a chain of bugs. The <span className="text-emerald-300">privilege escalation</span> step
          on the Linux servers was this CVE — a beautiful example of how a subtle confusion
          between two memory <em>spheres</em> turns into root.
        </div>
      </div>
    </section>
  );
};

const BackgroundGrid: React.FC = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <div
      className="absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage:
          'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at top, black 30%, transparent 70%)',
      }}
    />
    <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-emerald-500/10 blur-[140px]" />
    <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-rose-500/10 blur-[120px]" />
  </div>
);

const StatBadge: React.FC<{ label: string; value: string; sub?: string; mono?: boolean }> = ({
  label,
  value,
  sub,
  mono,
}) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur px-4 py-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`mt-1 text-base text-white ${mono ? 'font-mono' : ''}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

/* ============================== TL;DR ============================== */

const TldrSection: React.FC = () => {
  return (
    <Section eyebrow="The 60-second version" title="What actually happened">
      <div className="grid md:grid-cols-3 gap-4">
        <TldrCard
          step="1"
          title="Initial access"
          body="Attackers got a shell on a Canvas Linux box as a low-privileged service user. This is the part that does not need a kernel bug — phished creds, an exposed service, a stolen API token."
          tone="slate"
        />
        <TldrCard
          step="2"
          title="Kernel escalation"
          body={
            <>
              They opened an <span className="font-mono text-emerald-300">AF_ALG</span> socket and
              crafted a scatter-gather AEAD request whose buffers crossed two different memory
              mappings — triggering this CVE and corrupting kernel memory.
            </>
          }
          tone="emerald"
        />
        <TldrCard
          step="3"
          title="Game over"
          body="Memory corruption inside the kernel was leveraged into overwriting their process credentials. They became root, dumped the database, and exfiltrated student data."
          tone="rose"
        />
      </div>

      <div className="mt-8 text-sm text-slate-400 max-w-3xl">
        The rest of this page builds intuition for step 2 — the part you, as a CS 161 student, can
        actually reason about. You already know the crypto. You already know what a syscall is.
        We just need to glue those together and look closely at where the kernel got confused.
      </div>
    </Section>
  );
};

const TldrCard: React.FC<{
  step: string;
  title: string;
  body: React.ReactNode;
  tone: 'slate' | 'emerald' | 'rose';
}> = ({ step, title, body, tone }) => {
  const tones = {
    slate: 'border-slate-700 from-slate-800/60',
    emerald: 'border-emerald-500/30 from-emerald-500/10',
    rose: 'border-rose-500/30 from-rose-500/10',
  };
  const num = {
    slate: 'text-slate-500',
    emerald: 'text-emerald-300',
    rose: 'text-rose-300',
  };
  return (
    <div className={`relative rounded-2xl border ${tones[tone]} bg-gradient-to-b to-slate-900/40 p-6`}>
      <div className={`font-mono text-xs ${num[tone]} mb-3`}>STEP {step}</div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-slate-300 leading-relaxed">{body}</p>
    </div>
  );
};

/* ============================== SECTION SHELL ============================== */

const Section: React.FC<{
  eyebrow?: string;
  title: string;
  children: React.ReactNode;
  id?: string;
}> = ({ eyebrow, title, children, id }) => (
  <section id={id} className="px-4 sm:px-6 lg:px-8 py-24">
    <div className="max-w-5xl mx-auto">
      {eyebrow && (
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-300 mb-3">
          {eyebrow}
        </div>
      )}
      <h2 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-10">
        {title}
      </h2>
      {children}
    </div>
  </section>
);

/* ============================== SPHERES (userspace / kernel) ============================== */

const SpheresSection: React.FC = () => {
  const [crossing, setCrossing] = useState(false);

  return (
    <Section eyebrow="Concept · CWE-669" title="Two spheres, one trust boundary">
      <div className="grid lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-2 space-y-4 text-slate-300 leading-relaxed">
          <p>
            On any modern OS, code lives in one of two <em>spheres</em>: userspace (your shell,
            your browser, Canvas's Ruby app) and kernel space (the Linux kernel itself). They
            share the same physical RAM but the kernel decides which pages each side can touch.
          </p>
          <p>
            Userspace asks the kernel to do privileged things — read a file, send a packet,
            <em> encrypt some bytes</em> — through <span className="font-mono text-emerald-300">syscalls</span>.
            The kernel must validate every input, because <span className="text-white">the
            attacker is on the other side of that arrow</span>.
          </p>
          <p>
            CWE-669 — the bug class behind this CVE — is what happens when data crosses that
            boundary and the kernel mishandles which sphere it actually belongs to.
          </p>
          <button
            onClick={() => {
              setCrossing(false);
              requestAnimationFrame(() => setCrossing(true));
            }}
            className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm hover:bg-emerald-500/20 transition-colors"
          >
            ▶ Send a syscall across the boundary
          </button>
        </div>

        <div className="lg:col-span-3">
          <SpheresDiagram crossing={crossing} />
        </div>
      </div>
    </Section>
  );
};

const SpheresDiagram: React.FC<{ crossing: boolean }> = ({ crossing }) => {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-slate-900/60 p-6 overflow-hidden">
      <div className="grid grid-cols-2 gap-6">
        <Sphere
          title="Userspace"
          subtitle="ring 3"
          description="Your process, Canvas's web server, attacker's shell"
          color="sky"
        />
        <Sphere
          title="Kernel"
          subtitle="ring 0"
          description="Memory manager, schedulers, crypto, drivers"
          color="rose"
        />
      </div>

      {/* boundary line and gate */}
      <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-1 rounded text-[10px] font-mono bg-slate-950 border border-white/20 text-slate-300">
        syscall
      </div>

      {/* moving packet */}
      <div
        key={crossing ? 'go' : 'idle'}
        className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-emerald-300 shadow-[0_0_20px_rgba(110,231,183,0.8)] ${
          crossing ? 'animate-[crossover_1.6s_ease-in-out_forwards]' : 'opacity-0'
        }`}
        style={{ left: '15%' }}
      />
      <style>{`
        @keyframes crossover {
          0%   { left: 15%; opacity: 0; transform: translateY(-50%) scale(0.6); }
          15%  { opacity: 1; transform: translateY(-50%) scale(1); }
          50%  { left: 50%; }
          85%  { opacity: 1; }
          100% { left: 85%; opacity: 0; transform: translateY(-50%) scale(0.6); }
        }
      `}</style>
    </div>
  );
};

const Sphere: React.FC<{
  title: string;
  subtitle: string;
  description: string;
  color: 'sky' | 'rose';
}> = ({ title, subtitle, description, color }) => {
  const palette =
    color === 'sky'
      ? {
          ring: 'ring-sky-400/30',
          glow: 'bg-sky-500/10',
          dot: 'bg-sky-400',
          label: 'text-sky-200',
        }
      : {
          ring: 'ring-rose-400/30',
          glow: 'bg-rose-500/10',
          dot: 'bg-rose-400',
          label: 'text-rose-200',
        };
  return (
    <div className={`relative rounded-xl ring-1 ${palette.ring} bg-slate-950/60 p-5 h-44`}>
      <div className={`absolute -inset-1 rounded-xl ${palette.glow} blur-2xl -z-10`} />
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${palette.dot}`} />
        <div className={`text-xs font-mono ${palette.label}`}>{subtitle}</div>
      </div>
      <div className="mt-1 text-2xl font-semibold text-white">{title}</div>
      <div className="mt-2 text-xs text-slate-400">{description}</div>
    </div>
  );
};

/* ============================== AEAD REFRESHER ============================== */

const AeadSection: React.FC = () => {
  const [tampered, setTampered] = useState(false);
  const [decrypted, setDecrypted] = useState(false);

  // canonical bytes
  const ct = ['9F', '2A', 'BC', '11', '04', 'D7', 'E0', '6B'];
  const tamperedCt = [...ct];
  if (tampered) tamperedCt[3] = 'FF';

  return (
    <Section eyebrow="Reminder from CS 161" title="AEAD: encrypt and authenticate, together">
      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2 space-y-4 text-slate-300 leading-relaxed">
          <p>
            <span className="font-mono text-emerald-300">AEAD</span> = Authenticated Encryption
            with Associated Data. It's the primitive you'd reach for when you need both{' '}
            <span className="text-white">confidentiality</span> (no one reads your bytes) and{' '}
            <span className="text-white">integrity</span> (no one tampers with them).
          </p>
          <p>
            Concretely: <span className="font-mono">AES-GCM</span>,{' '}
            <span className="font-mono">ChaCha20-Poly1305</span>. Encrypt produces ciphertext{' '}
            <em>plus</em> an authentication tag. Decrypt verifies the tag before returning bytes —
            if it fails, you get nothing.
          </p>
          <p>
            Canvas's services use AEAD all over the place — TLS, cookies, internal RPC. The
            kernel exposes its own AEAD implementations to userspace through a socket family
            called <span className="font-mono text-emerald-300">AF_ALG</span>, which is where our
            story starts.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
            <div className="grid grid-cols-8 gap-3 items-center">
              <ByteBox label="Plaintext" bytes={['68', '69', '20', 'C', 'S', '1', '6', '1']} muted />
              <Arrow label="K, N, AD" />
              <div className="col-span-2 px-3 py-3 rounded-lg border border-emerald-400/30 bg-emerald-400/5 text-center">
                <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-widest">
                  AEAD Encrypt
                </div>
                <div className="text-xs text-slate-400 mt-1">AES-GCM</div>
              </div>
              <Arrow />
              <ByteBox
                label="Ciphertext"
                bytes={tamperedCt}
                tag={tampered ? 'FF·tampered' : 'A1·valid'}
                tone={tampered ? 'rose' : 'emerald'}
              />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <ToggleChip
                active={tampered}
                onClick={() => {
                  setTampered((v) => !v);
                  setDecrypted(false);
                }}
                label="Flip a bit in ciphertext"
              />
              <button
                onClick={() => setDecrypted(true)}
                className="text-xs font-mono px-3 py-1.5 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200"
              >
                ▶ Try to decrypt
              </button>
              <button
                onClick={() => {
                  setTampered(false);
                  setDecrypted(false);
                }}
                className="text-xs font-mono px-3 py-1.5 rounded-md border border-white/10 text-slate-400 hover:text-white"
              >
                reset
              </button>
            </div>

            {decrypted && (
              <div
                className={`mt-4 rounded-lg px-4 py-3 text-sm font-mono ${
                  tampered
                    ? 'bg-rose-500/10 border border-rose-500/30 text-rose-200'
                    : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
                }`}
              >
                {tampered
                  ? '✗  AUTH FAIL  —  tag did not verify, kernel returns -EBADMSG and discards plaintext.'
                  : '✓  decrypted: "hi CS161"  —  tag verified, plaintext released to userspace.'}
              </div>
            )}

            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Hold on to this picture. The kernel is the one running the AEAD box, and the
              attacker controls what goes into <em>and out of</em> it.
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};

const ByteBox: React.FC<{
  label: string;
  bytes: string[];
  tag?: string;
  tone?: 'emerald' | 'rose';
  muted?: boolean;
}> = ({ label, bytes, tag, tone, muted }) => {
  const toneCls =
    tone === 'rose'
      ? 'border-rose-500/40 bg-rose-500/5'
      : tone === 'emerald'
      ? 'border-emerald-500/40 bg-emerald-500/5'
      : 'border-white/10 bg-white/[0.03]';
  return (
    <div className={`col-span-2 rounded-lg border ${toneCls} p-3`}>
      <div className={`text-[10px] uppercase tracking-widest mb-2 ${muted ? 'text-slate-500' : 'text-slate-400'}`}>
        {label}
      </div>
      <div className="grid grid-cols-4 gap-1 text-[11px] font-mono text-slate-200">
        {bytes.map((b, i) => (
          <div
            key={i}
            className={`px-1 py-0.5 rounded text-center ${
              tone === 'rose' && i === 3 ? 'bg-rose-500/30 text-rose-100' : 'bg-slate-900/80'
            }`}
          >
            {b}
          </div>
        ))}
      </div>
      {tag && (
        <div className="mt-2 text-[10px] font-mono text-slate-400">
          tag: <span className={tone === 'rose' ? 'text-rose-300' : 'text-emerald-300'}>{tag}</span>
        </div>
      )}
    </div>
  );
};

const Arrow: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex flex-col items-center text-slate-500">
    {label && <div className="text-[9px] font-mono mb-1">{label}</div>}
    <div className="text-lg leading-none">→</div>
  </div>
);

const ToggleChip: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({
  active,
  onClick,
  label,
}) => (
  <button
    onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-md border transition-colors font-mono ${
      active
        ? 'bg-rose-500/15 border-rose-500/40 text-rose-200'
        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
    }`}
  >
    {active ? '● ' : '○ '}
    {label}
  </button>
);

/* ============================== algif_aead INTERFACE ============================== */

const AlgifSection: React.FC = () => {
  return (
    <Section eyebrow="The interface under attack" title="algif_aead: AEAD over a socket">
      <div className="grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-2 space-y-4 text-slate-300 leading-relaxed">
          <p>
            Linux exposes its kernel crypto primitives to userspace via a socket family called{' '}
            <span className="font-mono text-emerald-300">AF_ALG</span>. Want the kernel to encrypt
            something for you with AES-GCM? Open a socket, name a cipher, send your plaintext.
          </p>
          <p>
            The neat part — and where things get interesting — is that the kernel accepts data via{' '}
            <span className="font-mono text-emerald-300">scatter-gather</span> lists. Instead of
            one contiguous buffer, you can hand the kernel a list of <em>pointers to pages</em>{' '}
            (the iov array of <span className="font-mono">sendmsg</span>).
          </p>
          <p>
            That feature is normally a performance win. It also lets a userspace caller hand the
            kernel pointers from <em>different memory mappings</em> in a single request — which
            is exactly the corner the bug lives in.
          </p>
        </div>

        <div className="lg:col-span-3">
          <CodeBlock
            filename="userspace_attacker.c"
            lang="c"
            lines={[
              ['c', '// 1. open the AF_ALG socket and pick a cipher'],
              ['k', 'int', ' tfm = ', 'k', 'socket', '(AF_ALG, SOCK_SEQPACKET, 0);'],
              ['k', 'struct', ' sockaddr_alg sa = {'],
              ['  ', '.salg_family = AF_ALG,'],
              ['  ', '.salg_type   = "aead",'],
              ['  ', '.salg_name   = "', 's', 'gcm(aes)', 's', '",'],
              ['', '};'],
              ['k', 'bind', '(tfm, (struct sockaddr *)&sa, sizeof sa);'],
              ['', ''],
              ['c', '// 2. one operation = one accept()'],
              ['k', 'int', ' op = ', 'k', 'accept', '(tfm, NULL, NULL);'],
              ['', ''],
              ['c', '// 3. submit work via scatter-gather'],
              ['k', 'struct', ' iovec iov[2] = {'],
              ['  ', '{ .iov_base = page_from_mapping_A, .iov_len = 4096 },'],
              ['  ', '{ .iov_base = page_from_mapping_B, .iov_len = 4096 },  ', 'c', '// <-- the catch'],
              ['', '};'],
              ['k', 'struct', ' msghdr msg = { .msg_iov = iov, .msg_iovlen = 2 };'],
              ['k', 'sendmsg', '(op, &msg, 0);'],
              ['k', 'recvmsg', '(op, &reply, 0);'],
            ]}
          />
        </div>
      </div>
    </Section>
  );
};

/* token-style mini code block. types: k=keyword, c=comment, s=string, default=plain */
const CodeBlock: React.FC<{
  filename: string;
  lang: string;
  lines: Array<Array<string>>;
}> = ({ filename, lang, lines }) => {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/80 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-white/[0.03] border-b border-white/5">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        </div>
        <div className="font-mono text-xs text-slate-400">{filename}</div>
        <div className="font-mono text-[10px] text-slate-600 uppercase">{lang}</div>
      </div>
      <pre className="p-5 text-[12.5px] leading-6 font-mono overflow-x-auto">
        {lines.map((line, i) => {
          // Parse flat array as typed tokens.
          // Known type prefixes: 'k' (keyword), 'c' (comment), 's' (string), '' (plain).
          // Any element that is NOT a known type prefix is treated as a plain-text token itself.
          const tokens: Array<{ type: string; text: string }> = [];
          let idx = 0;
          while (idx < line.length) {
            const cur = line[idx];
            const TYPES = new Set(['k', 'c', 's', '']);
            if (TYPES.has(cur) && idx + 1 < line.length) {
              tokens.push({ type: cur, text: line[idx + 1] });
              idx += 2;
            } else {
              tokens.push({ type: '', text: cur });
              idx += 1;
            }
          }
          return (
            <div key={i} className="flex">
              <span className="select-none w-7 text-right pr-3 text-slate-700">{i + 1}</span>
              <span className="flex-1">
                {tokens.map((t, j) => {
                  const cls =
                    t.type === 'k'
                      ? 'text-fuchsia-300'
                      : t.type === 'c'
                      ? 'text-slate-500 italic'
                      : t.type === 's'
                      ? 'text-emerald-300'
                      : 'text-slate-200';
                  return (
                    <span key={j} className={cls}>
                      {t.text}
                    </span>
                  );
                })}
              </span>
            </div>
          );
        })}
      </pre>
    </div>
  );
};

/* ============================== IN-PLACE vs OUT-OF-PLACE ============================== */

const InPlaceSection: React.FC = () => {
  const [mode, setMode] = useState<'inplace' | 'outplace'>('inplace');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1400);
    return () => clearInterval(id);
  }, []);

  return (
    <Section eyebrow="Buffer geometry" title="In-place vs out-of-place encryption">
      <p className="max-w-3xl text-slate-300 leading-relaxed mb-8">
        When an AEAD implementation processes a buffer, it can either write the ciphertext{' '}
        <em>over</em> the plaintext (in-place — saves memory, faster) or into a fresh buffer
        (out-of-place — simpler invariants, harder to mess up). That choice is the entire ball
        game for this CVE.
      </p>

      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
        <div className="flex items-center gap-2 mb-6">
          <ModeButton
            active={mode === 'inplace'}
            onClick={() => setMode('inplace')}
            label="In-place"
            sub="src ≡ dst"
          />
          <ModeButton
            active={mode === 'outplace'}
            onClick={() => setMode('outplace')}
            label="Out-of-place"
            sub="src ≠ dst"
          />
        </div>

        <BufferDiagram mode={mode} tick={tick} />

        <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
          <div
            className={`rounded-lg p-4 border ${
              mode === 'inplace'
                ? 'border-emerald-500/30 bg-emerald-500/5'
                : 'border-white/10 bg-white/[0.02]'
            }`}
          >
            <div className="font-mono text-xs text-emerald-300 mb-1">In-place</div>
            <p className="text-slate-300">
              One buffer plays both roles. Halfway through, half the bytes are plaintext, half
              are ciphertext. <span className="text-white">Every step assumes a single, contiguous,
              well-defined region.</span>
            </p>
          </div>
          <div
            className={`rounded-lg p-4 border ${
              mode === 'outplace'
                ? 'border-emerald-500/30 bg-emerald-500/5'
                : 'border-white/10 bg-white/[0.02]'
            }`}
          >
            <div className="font-mono text-xs text-emerald-300 mb-1">Out-of-place</div>
            <p className="text-slate-300">
              Plaintext stays put; ciphertext lands in a separate output buffer. Slower, but the
              regions can be in totally different places without confusing the algorithm.
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};

const ModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  sub: string;
}> = ({ active, onClick, label, sub }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg border text-left transition-all ${
      active
        ? 'border-emerald-500/40 bg-emerald-500/10 text-white'
        : 'border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20'
    }`}
  >
    <div className="text-sm font-medium">{label}</div>
    <div className="text-[10px] font-mono text-slate-400">{sub}</div>
  </button>
);

const BufferDiagram: React.FC<{ mode: 'inplace' | 'outplace'; tick: number }> = ({ mode, tick }) => {
  // 12-byte mock buffer; "progress" cycles 0..12 then resets
  const N = 12;
  const progress = tick % (N + 4);
  const cells = useMemo(() => Array.from({ length: N }), []);

  return (
    <div className="space-y-6">
      {/* SRC */}
      <div>
        <div className="text-[10px] font-mono text-slate-500 mb-2 uppercase tracking-widest">
          src buffer (plaintext)
        </div>
        <div className="grid grid-cols-12 gap-1">
          {cells.map((_, i) => {
            const consumed = i < progress;
            return (
              <div
                key={i}
                className={`h-9 rounded transition-colors duration-300 flex items-center justify-center text-[10px] font-mono ${
                  consumed
                    ? mode === 'inplace'
                      ? 'bg-emerald-500/40 text-emerald-100'
                      : 'bg-sky-500/30 text-sky-100'
                    : 'bg-white/[0.06] text-slate-500'
                }`}
              >
                {consumed && mode === 'inplace' ? 'C' : 'P'}
              </div>
            );
          })}
        </div>
      </div>

      {/* arrow */}
      <div className="flex items-center gap-2 text-slate-400 text-xs font-mono">
        <span>aead_encrypt(key, nonce)</span>
        <span className="flex-1 h-px bg-gradient-to-r from-emerald-400/40 via-emerald-400/10 to-transparent" />
      </div>

      {/* DST — only shown in out-of-place */}
      {mode === 'outplace' && (
        <div>
          <div className="text-[10px] font-mono text-slate-500 mb-2 uppercase tracking-widest">
            dst buffer (ciphertext)
          </div>
          <div className="grid grid-cols-12 gap-1">
            {cells.map((_, i) => {
              const written = i < progress;
              return (
                <div
                  key={i}
                  className={`h-9 rounded transition-colors duration-300 flex items-center justify-center text-[10px] font-mono ${
                    written ? 'bg-emerald-500/40 text-emerald-100' : 'bg-white/[0.04] text-slate-600'
                  }`}
                >
                  {written ? 'C' : '·'}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mode === 'inplace' && (
        <div className="text-[11px] font-mono text-slate-500">
          ↑ same buffer — bytes flip from <span className="text-slate-300">P</span> to{' '}
          <span className="text-emerald-300">C</span> in place
        </div>
      )}
    </div>
  );
};

/* ============================== THE BUG ============================== */

const BugSection: React.FC = () => {
  const [stage, setStage] = useState(0); // 0..3
  const next = () => setStage((s) => Math.min(3, s + 1));
  const reset = () => setStage(0);

  return (
    <Section eyebrow="The actual bug" title="Two memory mappings, one operation">
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4 text-slate-300 leading-relaxed">
          <p>
            The kernel's <span className="font-mono text-emerald-300">algif_aead</span> code tried
            to be clever. If the source and destination of a request <em>looked</em> compatible,
            it would run the AEAD <span className="text-white">in-place</span> to save a copy.
          </p>
          <p>
            "Compatible" was checked too loosely. A scatter-gather list whose pages came from
            two different memory mappings <em>can</em> look like a single buffer if you only
            check page counts and offsets. It isn't.
          </p>
          <p>
            When the cipher then walked across that fake-contiguous region, in-place, it overwrote
            bytes belonging to a region the request never legitimately owned —{' '}
            <span className="text-rose-300">corrupting kernel state</span> the caller picked.
          </p>
          <p className="text-xs text-slate-500 mt-6">
            CWE-669 in plain English: data was treated as if it lived in one sphere of control,
            but actually came from a different one.
          </p>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
            <BugAnimation stage={stage} />
            <div className="mt-6 flex items-center gap-2">
              <button
                onClick={next}
                disabled={stage === 3}
                className="text-xs font-mono px-3 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ▶ Next step
              </button>
              <button
                onClick={reset}
                className="text-xs font-mono px-3 py-1.5 rounded-md border border-white/10 text-slate-400 hover:text-white"
              >
                reset
              </button>
              <div className="ml-auto text-[10px] font-mono text-slate-500">
                step {stage + 1} / 4
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const BUG_STAGES: Array<{ title: string; body: string }> = [
  {
    title: 'Userspace builds a scatter-gather list',
    body: 'iov[0] points into mapping A (a benign anonymous page). iov[1] points into mapping B (a page next to a privileged kernel-managed object). Both look like "just bytes" from the syscall surface.',
  },
  {
    title: 'Kernel decides in-place is safe',
    body: 'A weak compatibility check says: same length, same offsets, "contiguous enough" — green-light in-place AEAD. The check forgets that the two iovs come from different VMAs.',
  },
  {
    title: 'AEAD walks past the boundary',
    body: 'In-place encryption blasts ciphertext across both pages as if they were one buffer. Bytes are written into the second mapping with no regard for what actually lives there.',
  },
  {
    title: 'Kernel state corrupted',
    body: 'The bytes the attacker wrote land on top of a kernel data structure (a freelist pointer, a credentials struct, a function pointer in a slab) — primitives an attacker can turn into root.',
  },
];

const BugAnimation: React.FC<{ stage: number }> = ({ stage }) => {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 relative">
        {/* mapping A */}
        <div
          className={`rounded-lg border p-3 transition-all duration-500 ${
            stage >= 0 ? 'border-sky-400/40 bg-sky-500/5' : 'border-white/10'
          }`}
        >
          <div className="text-[10px] font-mono text-sky-300 uppercase tracking-widest mb-2">
            mapping A · iov[0]
          </div>
          <div className="grid grid-cols-6 gap-1">
            {Array.from({ length: 12 }).map((_, i) => {
              const filled = stage >= 2;
              return (
                <div
                  key={i}
                  className={`h-5 rounded transition-colors ${
                    filled ? 'bg-emerald-400/40' : 'bg-white/[0.06]'
                  }`}
                />
              );
            })}
          </div>
          <div className="mt-2 text-[10px] text-slate-500 font-mono">attacker page</div>
        </div>
        {/* mapping B */}
        <div
          className={`rounded-lg border p-3 transition-all duration-500 ${
            stage >= 0 ? 'border-rose-400/40 bg-rose-500/5' : 'border-white/10'
          }`}
        >
          <div className="text-[10px] font-mono text-rose-300 uppercase tracking-widest mb-2">
            mapping B · iov[1]
          </div>
          <div className="grid grid-cols-6 gap-1">
            {Array.from({ length: 12 }).map((_, i) => {
              const overwritten = stage >= 2 && i < 8;
              const corrupted = stage >= 3 && i >= 4 && i < 10;
              return (
                <div
                  key={i}
                  className={`h-5 rounded transition-colors ${
                    corrupted
                      ? 'bg-rose-400/70 ring-1 ring-rose-200/60 animate-pulse'
                      : overwritten
                      ? 'bg-emerald-400/40'
                      : 'bg-white/[0.06]'
                  }`}
                />
              );
            })}
          </div>
          <div className="mt-2 text-[10px] text-slate-500 font-mono">
            adjacent kernel struct
          </div>
        </div>

        {/* the "contiguous" lie */}
        {stage >= 1 && (
          <div className="absolute -bottom-3 left-0 right-0 flex items-center gap-2 text-[10px] font-mono text-amber-300 justify-center">
            <span className="h-px flex-1 bg-amber-300/30" />
            <span>kernel believes: one contiguous buffer</span>
            <span className="h-px flex-1 bg-amber-300/30" />
          </div>
        )}
      </div>

      {/* AEAD pass animation strip */}
      {stage >= 2 && (
        <div className="relative h-8 rounded-md border border-white/10 bg-white/[0.03] overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400/0 via-emerald-400/40 to-emerald-400/0"
            style={{ width: '40%', animation: 'sweep 1.6s linear infinite' }}
          />
          <div className="relative h-full flex items-center justify-center text-[11px] font-mono text-slate-300">
            in-place AEAD pass — sweeping bytes
          </div>
          <style>{`
            @keyframes sweep {
              0%   { transform: translateX(-50%); }
              100% { transform: translateX(250%); }
            }
          `}</style>
        </div>
      )}

      <div className="rounded-lg border border-white/10 bg-slate-950/60 px-4 py-3">
        <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-widest mb-1">
          step {stage + 1}
        </div>
        <div className="text-sm font-medium text-white">{BUG_STAGES[stage].title}</div>
        <p className="mt-1 text-sm text-slate-400 leading-relaxed">{BUG_STAGES[stage].body}</p>
      </div>
    </div>
  );
};

/* ============================== EXPLOIT CHAIN ============================== */

const CHAIN_STEPS = [
  {
    title: 'Foothold',
    sub: 'as a service user',
    body: 'Attackers begin with a low-privileged shell on a Canvas Linux server. No kernel code runs as them yet — they are just a normal process behind the trust boundary.',
    glyph: '$',
  },
  {
    title: 'Spray-shape the heap',
    sub: 'kmalloc grooming',
    body: 'They allocate and free objects in the kernel slab that sits next to where they expect their AEAD destination buffer to land. Common targets: a struct holding function pointers or credentials.',
    glyph: '▦',
  },
  {
    title: 'Open AF_ALG, request gcm(aes)',
    sub: 'algif_aead engaged',
    body: 'They open the algif_aead socket, bind to AES-GCM, and accept() a per-op handle.',
    glyph: '⌥',
  },
  {
    title: 'Submit poisoned scatter-gather',
    sub: 'iov[0] = A, iov[1] = B',
    body: 'sendmsg() with two iovecs from two different mappings — chosen so that the kernel\'s loose check decides an in-place pass is fine.',
    glyph: '⇉',
  },
  {
    title: 'Cipher writes outside its sphere',
    sub: 'CWE-669 fires',
    body: 'The in-place AEAD pass walks past the legitimate destination and overwrites adjacent kernel memory with attacker-chosen bytes.',
    glyph: '✷',
  },
  {
    title: 'Pivot corruption → privilege',
    sub: 'overwrite cred, become root',
    body: 'A classic primitive: zero out the uid/gid fields of the calling task\'s credential struct. The kernel now believes the attacker is root.',
    glyph: '#',
  },
  {
    title: 'Exfiltrate',
    sub: 'database, secrets, tokens',
    body: 'With root, every Canvas service on the box is reachable. The attackers dump database contents, SSH keys, and API tokens — the part the public sees in the news.',
    glyph: '⇡',
  },
];

const ExploitChainSection: React.FC = () => {
  const [active, setActive] = useState(0);
  return (
    <Section eyebrow="From bug to root" title="The exploitation chain, one click at a time">
      <div className="grid lg:grid-cols-12 gap-6">
        <ol className="lg:col-span-5 space-y-2">
          {CHAIN_STEPS.map((s, i) => (
            <li key={i}>
              <button
                onClick={() => setActive(i)}
                className={`w-full text-left rounded-lg px-4 py-3 border transition-all flex items-center gap-3 ${
                  active === i
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
                }`}
              >
                <span
                  className={`w-7 h-7 grid place-items-center rounded-md font-mono text-sm ${
                    active === i
                      ? 'bg-emerald-400/20 text-emerald-200'
                      : 'bg-white/5 text-slate-400'
                  }`}
                >
                  {s.glyph}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-white">{s.title}</span>
                  <span className="block text-[11px] font-mono text-slate-500">{s.sub}</span>
                </span>
                <span
                  className={`text-[10px] font-mono ${
                    active === i ? 'text-emerald-300' : 'text-slate-600'
                  }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
              </button>
            </li>
          ))}
        </ol>

        <div className="lg:col-span-7">
          <div className="sticky top-20 rounded-2xl border border-white/10 bg-slate-900/60 p-6 min-h-[20rem]">
            <div className="text-[10px] font-mono text-emerald-300 uppercase tracking-widest mb-2">
              step {active + 1} of {CHAIN_STEPS.length}
            </div>
            <h3 className="text-2xl font-semibold text-white">{CHAIN_STEPS[active].title}</h3>
            <div className="text-sm font-mono text-slate-400 mt-1">{CHAIN_STEPS[active].sub}</div>
            <p className="mt-4 text-slate-300 leading-relaxed">{CHAIN_STEPS[active].body}</p>

            {/* progress bar */}
            <div className="mt-8">
              <div className="h-1 w-full rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-rose-400 transition-all duration-500"
                  style={{ width: `${((active + 1) / CHAIN_STEPS.length) * 100}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-mono text-slate-500">
                <span>foothold</span>
                <span>root</span>
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button
                onClick={() => setActive((a) => Math.max(0, a - 1))}
                disabled={active === 0}
                className="text-xs font-mono px-3 py-1.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40"
              >
                ← prev
              </button>
              <button
                onClick={() => setActive((a) => Math.min(CHAIN_STEPS.length - 1, a + 1))}
                disabled={active === CHAIN_STEPS.length - 1}
                className="text-xs font-mono px-3 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 disabled:opacity-40"
              >
                next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ============================== THE PATCH ============================== */

const PatchSection: React.FC = () => {
  return (
    <Section eyebrow="The fix" title="Revert the cleverness">
      <p className="max-w-3xl text-slate-300 leading-relaxed mb-8">
        The maintainers' fix is almost anticlimactic: stop trying to do AEAD in-place when the
        source might span foreign mappings. Always run out-of-place, and copy the associated data
        explicitly. Slower; correct.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <DiffPane
          title="Before · vulnerable"
          tone="rose"
          lines={[
            { kind: 'ctx', text: 'static int aead_recvmsg(struct sock *sk, ...)' },
            { kind: 'ctx', text: '{' },
            { kind: 'ctx', text: '    ...' },
            { kind: 'del', text: '    if (in_place_possible(req)) {' },
            { kind: 'del', text: '        do_inplace_aead(req);' },
            { kind: 'del', text: '    } else {' },
            { kind: 'del', text: '        do_outplace_aead(req);' },
            { kind: 'del', text: '    }' },
            { kind: 'ctx', text: '    ...' },
            { kind: 'ctx', text: '}' },
          ]}
        />
        <DiffPane
          title="After · CVE-2026-31431 patch"
          tone="emerald"
          lines={[
            { kind: 'ctx', text: 'static int aead_recvmsg(struct sock *sk, ...)' },
            { kind: 'ctx', text: '{' },
            { kind: 'ctx', text: '    ...' },
            { kind: 'add', text: '    /* always out-of-place; copy AD explicitly */' },
            { kind: 'add', text: '    do_outplace_aead(req);' },
            { kind: 'add', text: '    copy_associated_data(req);' },
            { kind: 'ctx', text: '    ...' },
            { kind: 'ctx', text: '}' },
          ]}
        />
      </div>

      <div className="mt-8 rounded-xl border border-amber-500/20 bg-amber-500/5 p-5">
        <div className="font-mono text-xs text-amber-300 uppercase tracking-widest mb-1">
          design takeaway
        </div>
        <p className="text-slate-300 text-sm leading-relaxed">
          The <em>optimization</em> wasn't wrong on its own — it was wrong because the safety
          check guarding it was incomplete. The cheapest, most defensible fix is often "remove the
          fast path" rather than "harden the check." When there's a trust boundary involved, the
          slow-and-obviously-correct path tends to age better.
        </p>
      </div>
    </Section>
  );
};

const DiffPane: React.FC<{
  title: string;
  tone: 'rose' | 'emerald';
  lines: Array<{ kind: 'ctx' | 'add' | 'del'; text: string }>;
}> = ({ title, tone, lines }) => {
  const ring = tone === 'rose' ? 'border-rose-500/30' : 'border-emerald-500/30';
  const accent = tone === 'rose' ? 'text-rose-300' : 'text-emerald-300';
  return (
    <div className={`rounded-2xl border ${ring} bg-slate-950/80 overflow-hidden`}>
      <div className={`px-4 py-2 border-b border-white/5 text-xs font-mono ${accent}`}>{title}</div>
      <pre className="text-[12.5px] leading-6 font-mono p-4 overflow-x-auto">
        {lines.map((l, i) => {
          const sign = l.kind === 'add' ? '+' : l.kind === 'del' ? '-' : ' ';
          const cls =
            l.kind === 'add'
              ? 'text-emerald-200 bg-emerald-500/10'
              : l.kind === 'del'
              ? 'text-rose-200 bg-rose-500/10'
              : 'text-slate-300';
          return (
            <div key={i} className={`flex ${cls}`}>
              <span className="select-none w-6 text-right pr-3 text-slate-600">{sign}</span>
              <span className="flex-1 whitespace-pre">{l.text}</span>
            </div>
          );
        })}
      </pre>
    </div>
  );
};

/* ============================== LESSONS ============================== */

const LessonsSection: React.FC = () => {
  return (
    <Section eyebrow="If you remember nothing else" title="Five takeaways for CS 161">
      <div className="grid md:grid-cols-2 gap-4">
        <Lesson
          n="01"
          title="Authenticated encryption is not memory safety."
          body="AES-GCM verifies that bytes weren't tampered with. It says nothing about who is allowed to write to the buffer those bytes live in. Memory safety is a separate property — you have to enforce it yourself, especially across trust boundaries."
        />
        <Lesson
          n="02"
          title="Trust boundaries are physical, not just logical."
          body="Userspace and kernel share RAM. The boundary is enforced by checks the kernel chooses to make. Every input from across the boundary — including pointers in a syscall — is hostile until proven otherwise."
        />
        <Lesson
          n="03"
          title="Optimizations near a trust boundary are dangerous."
          body="The 'clever' in-place fast path was the entire bug. When the consequence of a wrong check is privilege escalation, the right default is the boring path. Hardening the optimization came with a footgun the maintainers ultimately removed."
        />
        <Lesson
          n="04"
          title="Compromise is a chain, not a vulnerability."
          body="ShinyHunters didn't 'use one CVE to hack Canvas.' They chained an initial-access bug, a misconfiguration, this kernel CVE, and an exfiltration path. Defenders win by breaking any link — patch promptly, segment networks, monitor egress."
        />
        <Lesson
          n="05"
          title="Read CVEs the way you read CS 161 problems."
          body="Identify the trust boundary, name the assumption being violated, sketch the attacker's capabilities before and after, and find the smallest fix that re-establishes the invariant. That's the entire skill."
          full
        />
      </div>
    </Section>
  );
};

const Lesson: React.FC<{ n: string; title: string; body: string; full?: boolean }> = ({
  n,
  title,
  body,
  full,
}) => (
  <div
    className={`rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-emerald-500/30 hover:bg-emerald-500/[0.04] transition-colors ${
      full ? 'md:col-span-2' : ''
    }`}
  >
    <div className="font-mono text-xs text-emerald-300 mb-3">{n}</div>
    <h3 className="text-lg font-semibold text-white mb-2 leading-snug">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
  </div>
);

/* ============================== FOOTER ============================== */

const PageFooter: React.FC = () => {
  return (
    <footer className="border-t border-white/5 mt-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-2 gap-8 text-sm">
        <div>
          <div className="text-white font-medium mb-2">References</div>
          <ul className="space-y-1 text-slate-400">
            <li>
              <a
                className="hover:text-emerald-300"
                href="https://nvd.nist.gov/vuln/detail/CVE-2026-31431"
                target="_blank"
                rel="noreferrer"
              >
                NVD · CVE-2026-31431
              </a>
            </li>
            <li>
              <a
                className="hover:text-emerald-300"
                href="https://cwe.mitre.org/data/definitions/669.html"
                target="_blank"
                rel="noreferrer"
              >
                CWE-669 · Incorrect Resource Transfer Between Spheres
              </a>
            </li>
            <li>
              <a
                className="hover:text-emerald-300"
                href="https://sp26.cs161.org/"
                target="_blank"
                rel="noreferrer"
              >
                CS 161 · Computer Security · Spring 2026
              </a>
            </li>
          </ul>
        </div>
        <div className="md:text-right">
          <div className="text-slate-500 text-xs font-mono">
            Written for CS 161 students by Nazar Ospanov · TA, Spring 2026
          </div>
          <a
            href="/"
            className="inline-block mt-3 text-emerald-300 hover:text-emerald-200 text-sm"
          >
            ← back to nazar-ospanov.github.io
          </a>
        </div>
      </div>
    </footer>
  );
};
